import { observer } from 'mobx-react';
import { useState } from 'react';
import {
  Box,
  Collapse,
  Flex,
  Image,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useWaitingStateStore } from '../../../../store';
import { MyIcon } from '@web/common-ui';
import { DEFAULT_BMS_SAMPLE_GRAMS } from '@web/common';
import flowersOneGramm from '../../../../assets/bdm/flowers-1gramm.png';
import haschischOneGramm from '../../../../assets/bdm/haschisch-1gramm.png';
import flowersTwoGramm from '../../../../assets/bdm/flowers 2gramm_v2.png';
import haschischTwoGramm from '../../../../assets/bdm/haschisch 2gramm_v2.png';

const COLLAPSE_ID = 'bms-reminder-collapse';

// the requested amount depends on the participant's cohort — only 1 g and 2 g exist
const BMS_IMAGES_ONE_GRAMM = {
  flowers: flowersOneGramm,
  hashish: haschischOneGramm,
};
const BMS_IMAGES_TWO_GRAMM = {
  flowers: flowersTwoGramm,
  hashish: haschischTwoGramm,
};

export const BmsReminderBanner = observer(() => {
  const { t } = useTranslation('waiting-screen');
  const store = useWaitingStateStore();
  const [expanded, setExpanded] = useState(false);

  if (!store.isBmsPending) return null;

  const grams = store.bmsSample?.sampleGrams ?? DEFAULT_BMS_SAMPLE_GRAMS;
  const images = grams === 2 ? BMS_IMAGES_TWO_GRAMM : BMS_IMAGES_ONE_GRAMM;

  return (
    <Box
      p="12px 20px 12px 16px"
      w="100%"
      bg="#FBFBF8"
      style={{
        borderLeft: '4px solid #e8a317',
        borderBottom: '1px solid #edece8',
      }}
    >
      <UnstyledButton
        onClick={() => setExpanded((prev) => !prev)}
        w="100%"
        display="flex"
        style={{ alignItems: 'center' }}
        type="button"
        aria-expanded={expanded}
        aria-controls={COLLAPSE_ID}
      >
        <Flex justify="space-between" align="center" w="100%">
          <Flex align="center" gap="12px">
            <Flex
              w={42}
              align="center"
              justify="center"
              style={{ flexShrink: 0 }}
            >
              <MyIcon name="info" style={{ width: 28, height: 28 }} />
            </Flex>
            <Text fw={700} size="16px" c="#202020" lh="1">
              {t('BmsReminder.Title')}
            </Text>
            <Text size="16px" c="#666" visibleFrom="sm" lh="1">
              {t('BmsReminder.ShortDescription', { grams })}
            </Text>
          </Flex>
          <MyIcon
            name={expanded ? 'arrow_up' : 'arrow_down'}
            style={{ flexShrink: 0, display: 'block' }}
          />
        </Flex>
      </UnstyledButton>

      <Collapse in={expanded} id={COLLAPSE_ID}>
        {expanded && (
          <Box pt="16px">
            <Text size="16px" c="#202020" mb="16px" lh="1.6">
              {t('BmsReminder.Description', { grams })}
            </Text>
            <Flex
              gap="14px"
              align="flex-end"
              direction={{ base: 'column', sm: 'row' }}
            >
              <Image
                src={images.flowers}
                w={{ base: '100%', sm: 280 }}
                fit="contain"
                radius={4}
                loading="lazy"
                alt={t('BmsReminder.ImageAltFlowers', { grams })}
              />
              <Image
                src={images.hashish}
                w={{ base: '100%', sm: 280 }}
                fit="contain"
                radius={4}
                loading="lazy"
                alt={t('BmsReminder.ImageAltHashish', { grams })}
              />
            </Flex>
            <Text size="14px" c="#888" mt="6px">
              {t('BmsReminder.ImageCaption', { grams })}
            </Text>
            <Text size="16px" c="#202020" mt="16px" lh="1.6">
              {t('BmsReminder.DescriptionAfterImages')}
            </Text>
          </Box>
        )}
      </Collapse>
    </Box>
  );
});
